import React, { useState, useEffect } from "react";
import axios from "axios";
import { FaUpload, FaEye, FaCalendarAlt, FaFileMedical, FaCheckCircle, FaTimesCircle, FaInbox, FaShieldAlt, FaInfoCircle } from "react-icons/fa";
import { API_BASE_URL, API_DOMAIN } from "../utils/config";


const MedicalCertificate = () => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [formData, setFormData] = useState({
    registrationDate: "",
    expiryDate: "",
    document: null,
  });

  const userRole = localStorage.getItem("userRole");
  const employeeData = JSON.parse(localStorage.getItem("employeeData"));
  
  let userId = "";
  let userName = "";
  let isCandidate = userRole === "candidate";

  if (isCandidate) {
    userId = localStorage.getItem("candidateId");
    userName = localStorage.getItem("candidateName");
  } else {
    userId = employeeData?.employeeId;
    userName = employeeData?.name;
  }

  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    if (userId && userId !== "undefined" && userId !== "null") {
      fetchCertificates();
      fetchNotifications();
    } else {
      setError("Unable to identify current user. Please log out and log in again.");
    }
  }, [userId]);

  const fetchCertificates = async () => {
    try {
      setLoading(true);
      if (userId === "undefined" || userId === "null") return;
      const res = await axios.get(`${API_BASE_URL}/medical-certificates/employee/${userId}`);
      if (res.data.success) {
        setCertificates(res.data.data);
      }
    } catch (err) {
      console.error("Fetch certificates error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      if (!userId || userId === "undefined" || userId === "null") return;
      const res = await axios.get(`${API_BASE_URL}/medical-certificates/notifications/${userId}`);
      if (res.data?.success) {
        setNotifications(res.data.data);
      }
    } catch (err) {
      // Silently ignore - notifications are non-critical
      setNotifications([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, document: e.target.files[0] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId || userId === "undefined" || userId === "null") {
      alert("Session expired or invalid user. Please login again.");
      return;
    }
    if (!formData.registrationDate || !formData.expiryDate || !formData.document) {
      alert("Please fill all fields and select a document.");
      return;
    }

    try {
      setUploading(true);
      setError("");
      setSuccess("");

      const data = new FormData();
      if (isCandidate) {
        data.append("candidateId", userId);
        data.append("candidateName", userName);
      } else {
        data.append("employeeId", userId);
        data.append("employeeName", userName);
      }
      data.append("registrationDate", formData.registrationDate);
      data.append("expiryDate", formData.expiryDate);
      data.append("document", formData.document);

      const res = await axios.post(`${API_BASE_URL}/medical-certificates/upload`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.success) {
        setSuccess("Medical certificate uploaded successfully!");
        setFormData({ registrationDate: "", expiryDate: "", document: null });
        fetchCertificates();
      }
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.response?.data?.message || "Failed to upload certificate.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="w-full min-h-screen p-4 space-y-6">

      {/* ── Header ── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100/50 shadow-sm">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-md">
              <FaFileMedical size={20} />
            </div>
          </div>
          <div>
            <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-green-500 to-blue-600 tracking-tight">MEDICAL CERTIFICATE</h1>
            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Health & Compliance Records Vault</div>
          </div>
        </div>
      </div>

      {/* ── Notifications / Reminders ── */}
      {notifications.length > 0 && (
        <div className="space-y-3">
          {notifications.map((note) => (
            <div key={note._id} className="bg-rose-50 border border-rose-100 p-4 rounded-2xl flex items-center gap-4 animate-pulse">
              <div className="p-2 bg-rose-600 text-white rounded-lg shadow-lg">
                <FaInfoCircle size={14} />
              </div>
              <div className="flex-1">
                <p className="text-[11px] font-black text-rose-700 uppercase tracking-widest leading-tight">Administrative Alert</p>
                <p className="text-xs font-bold text-rose-900 mt-1">{note.message}</p>
              </div>
              <div className="text-[9px] font-bold text-rose-400 uppercase tracking-widest">
                {new Date(note.createdAt).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Upload Form ── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
        {/* <h2 className="text-sm font-bold text-gray-700 uppercase tracking-widest mb-6 flex items-center gap-2">
          <FaUpload className="text-blue-500" /> Submit New Certificate
        </h2> */}

        <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[160px] space-y-2">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1.5 ml-1">
              <FaCalendarAlt className="text-blue-500" /> Registration Date
            </label>
            <input
              type="date"
              name="registrationDate"
              value={formData.registrationDate}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-gray-50/50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all text-sm font-bold outline-none"
              required
            />
          </div>

          <div className="flex-1 min-w-[160px] space-y-2">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1.5 ml-1">
              <FaCalendarAlt className="text-red-400" /> Expiry Date
            </label>
            <input
              type="date"
              name="expiryDate"
              value={formData.expiryDate}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-gray-50/50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all text-sm font-bold outline-none"
              required
            />
          </div>

          <div className="flex-1 min-w-[200px] space-y-2">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Upload Certificate</label>
            <div className="relative">
              <input
                type="file"
                onChange={handleFileChange}
                className="hidden"
                id="doc-upload"
                accept=".pdf,.jpg,.jpeg,.png"
              />
              <label
                htmlFor="doc-upload"
                className={`flex items-center gap-3 px-4 py-3 border-2 border-dashed rounded-xl transition-all cursor-pointer ${
                  formData.document
                    ? "border-blue-400 bg-blue-50/30"
                    : "border-gray-200 hover:border-blue-300 hover:bg-gray-50"
                }`}
              >
                <FaUpload className={`text-sm flex-shrink-0 ${formData.document ? "text-blue-500" : "text-gray-300"}`} />
                <span className="text-[10px] font-bold text-gray-600 uppercase tracking-widest truncate">
                  {formData.document ? formData.document.name : "Choose File (PDF / Image)"}
                </span>
              </label>
            </div>
          </div>

          <div className="flex-shrink-0">
            <button
              type="submit"
              disabled={uploading}
              className="h-[46px] px-8 bg-gradient-to-r from-green-500 to-blue-600 text-white rounded-xl text-[10px] font-bold uppercase tracking-[0.2em] shadow-lg shadow-blue-100 hover:scale-[1.02] transition-all active:scale-95 disabled:opacity-50 whitespace-nowrap flex items-center gap-2"
            >
              <FaUpload size={12} />
              {uploading ? "Uploading..." : "Submit"}
            </button>
          </div>
        </form>

        {success && (
          <div className="mt-4 p-4 bg-emerald-50 border border-emerald-100 rounded-2xl flex items-center gap-3">
            <FaCheckCircle className="text-emerald-500 text-sm flex-shrink-0" />
            <p className="text-[11px] font-bold text-emerald-700 uppercase tracking-widest">{success}</p>
          </div>
        )}
        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-center gap-3">
            <FaTimesCircle className="text-red-500 text-sm flex-shrink-0" />
            <p className="text-[11px] font-bold text-red-700 uppercase tracking-widest">{error}</p>
          </div>
        )}
      </div>

      {/* ── Records Table ── */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="text-[11px] text-white bg-gradient-to-r from-green-500 to-blue-600 uppercase tracking-widest font-bold">
              <tr>
                <th className="py-4 px-6 text-left font-bold">Registration Date</th>
                <th className="py-4 px-6 text-left font-bold">Expiry Date</th>
                <th className="py-4 px-6 text-center font-bold">Validity Status</th>
                <th className="py-4 px-6 text-center font-bold">Document Access</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr>
                  <td colSpan={4} className="py-16 text-center">
                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest animate-pulse">Loading Records...</p>
                  </td>
                </tr>
              ) : certificates.length > 0 ? (
                certificates.map((cert) => {
                  const isExpired = new Date(cert.expiryDate) < new Date();
                  return (
                    <tr key={cert._id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="py-4 px-6">
                        <div className="flex flex-col">
                          <span className="text-xs font-bold text-gray-800 uppercase tracking-tight">
                            {new Date(cert.registrationDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                          </span>
                          <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Issue Date</span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex flex-col">
                          <span className="text-xs font-bold text-gray-800 uppercase tracking-tight">
                            {new Date(cert.expiryDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                          </span>
                          <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Valid Until</span>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-center">
                        {isExpired ? (
                          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-50 text-red-600 text-[10px] font-bold uppercase tracking-widest border border-red-100 shadow-sm">
                            <FaTimesCircle size={11} /> Expired
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-bold uppercase tracking-widest border border-emerald-100 shadow-sm">
                            <FaCheckCircle size={11} /> Active
                          </span>
                        )}
                      </td>
                      <td className="py-4 px-6 text-center">
                        <a
                          href={`${API_DOMAIN}${cert.documentUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 inline-flex items-center justify-center rounded-xl bg-indigo-50 text-indigo-700 hover:bg-indigo-600 hover:text-white border border-indigo-100 transition-all transform active:scale-90 shadow-sm"
                          title="View Certificate"
                        >
                          <FaEye size={16} />
                        </a>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={4}>
                    <div className="p-16 text-center">
                      <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4 text-blue-200 border border-blue-100/50 shadow-sm">
                        <FaFileMedical size={30} />
                      </div>
                      <h3 className="text-base font-bold text-gray-700 mb-1">No Certificates Uploaded</h3>
                      <p className="text-[11px] text-gray-400 font-bold tracking-widest uppercase max-w-xs mx-auto leading-relaxed">Use the form above to submit your first medical certificate for review.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-center gap-2 text-[9px] font-bold text-gray-400 uppercase tracking-widest pt-2 pb-6">
        <FaShieldAlt className="text-blue-400" />
        Timely Health Medical Records Vault • {new Date().getFullYear()}
      </div>
    </div>
  );
};

export default MedicalCertificate;